import { useEffect, useRef, useState } from "react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ItemGraficoGerencial, SerieGrafico } from "../../../types";

type GraficoBarrasProps = {
  dados: ItemGraficoGerencial[];
  series: SerieGrafico[];
  formatarComoMoeda?: boolean;
  altura?: number;
};

type DimensoesGrafico = {
  largura: number;
  altura: number;
};

const formatarMoeda = (valor: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(valor);

const formatarNumero = (valor: number) =>
  new Intl.NumberFormat("pt-BR").format(valor);

const formatarEixo = (valor: number) =>
  new Intl.NumberFormat("pt-BR", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(valor);

export function GraficoBarras({
  dados,
  series,
  formatarComoMoeda = false,
  altura = 540,
}: GraficoBarrasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [dimensoes, setDimensoes] = useState<DimensoesGrafico>({
    largura: 0,
    altura,
  });

  useEffect(() => {
    const elemento = containerRef.current;

    if (!elemento) {
      return;
    }

    const atualizarDimensoes = () => {
      const largura = Math.floor(elemento.getBoundingClientRect().width);

      if (largura > 0) {
        setDimensoes({
          largura,
          altura,
        });
      }
    };

    /*
      Mede depois que o portal e os estilos
      terminarem a primeira renderização.
    */
    const frame1 = elemento.ownerDocument.defaultView?.requestAnimationFrame(
      () => {
        elemento.ownerDocument.defaultView?.requestAnimationFrame(
          atualizarDimensoes,
        );
      },
    );

    const timer = window.setTimeout(atualizarDimensoes, 100);

    const observer = new ResizeObserver(() => {
      atualizarDimensoes();
    });

    observer.observe(elemento);

    atualizarDimensoes();

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);

      if (frame1 !== undefined && elemento.ownerDocument.defaultView) {
        elemento.ownerDocument.defaultView.cancelAnimationFrame(frame1);
      }
    };
  }, [altura]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: `${altura}px`,
        minWidth: 0,
      }}
    >
      {dimensoes.largura > 0 && (
        <BarChart
          width={dimensoes.largura}
          height={dimensoes.altura}
          data={dados}
          margin={{
            top: 10,
            right: 20,
            left: 10,
            bottom: 10,
          }}
          barCategoryGap="18%"
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis dataKey="periodo" interval={0} />

          <YAxis
            allowDecimals={formatarComoMoeda}
            tickFormatter={(valor) => formatarEixo(Number(valor))}
          />

          <Tooltip
            formatter={(valor, nome) => [
              formatarComoMoeda
                ? formatarMoeda(Number(valor))
                : formatarNumero(Number(valor)),
              String(nome),
            ]}
          />

          <Legend />

          {series.map((serie) => (
            <Bar
              key={serie.dataKey}
              dataKey={serie.dataKey}
              name={serie.nome}
              fill={serie.cor}
              maxBarSize={70}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
          ))}

        </BarChart>
      )}
    </div>
  );
}
